/**
 * `/agents/verification-ops` — §8 scorecard M1–M12 + verification ops tiles.
 *
 * Read-only operator view for the verification-loop program: honest-N scorecard
 * cells, holdout ledger summary, admission ledger, last kill-switch drill,
 * export.emitted audit count, open-weights / grandfather share from models.lock.
 *
 * ## UI policy
 *
 * Extends existing blocks only (PageHeader, StatStrip, Card, DataTable,
 * StatusBadge, IntentBadge) — no hand-rolled atoms (UI-BRIEF). Placed under
 * the Agents mode rail next to Health; does not invent a new mode.
 *
 * ## Honest N
 *
 * Every number on this page is either measured or blocked with a reason.
 * Never invents zeros for missing vault / ledger / harness rows.
 */

import { createRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  StatStrip,
  StatusBadge,
  formatRelativeTime,
  type DataTableColumn,
  type StatStripItem,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  verificationOps,
  type ScorecardMetricCellDto,
  type VerificationOpsViewDto,
  type HoldoutTierDto,
} from "@excellent/ui/api/contracts/verification-ops";
import { rootRoute } from "./__root";

const NUM = "tabular-nums";
const MUTED = "type-subtext text-muted-foreground";

function AgentsVerificationOpsPage() {
  const { data, error, isLoading } = useApi(verificationOps.get, {});

  const oracleError = error
    ? error instanceof Error
      ? error.message
      : String(error)
    : data && !data.ok
      ? data.error
      : null;

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Verification ops"
        description="§8 scorecard M1–M12, holdout ledger, admission, kill-switch drill, export audit, and open-weights lock — every figure is measured or blocked with a reason."
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">
          Reading scorecard sources and verification ledgers…
        </p>
      ) : oracleError !== null || !data?.ok ? (
        <Card>
          <CardHeader>
            <CardTitle>Verification ops unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="type-body text-muted-foreground">
              Could not assemble the verification-ops payload. Ensure the vault
              and core dist are available, or read the blocked reason below.
            </p>
            <p className={`${MUTED} mt-2 break-words`}>{oracleError}</p>
          </CardContent>
        </Card>
      ) : (
        <VerificationOpsBody status={data.status} />
      )}
    </div>
  );
}

function metricStatusBadge(cell: ScorecardMetricCellDto) {
  if (cell.blocked) {
    return (
      <StatusBadge domain="verification_run" status="timeout" label="N / blocked" withDot />
    );
  }
  return (
    <StatusBadge domain="verification_run" status="completed" label="Measured" withDot />
  );
}

function killSwitchRunStatus(
  phase: string | null,
): "running" | "completed" | "error" | "timeout" | "superseded" {
  if (phase === "active" || phase === "armed-waiting") return "running";
  if (phase === "completed") return "completed";
  if (phase === "aborted") return "error";
  return "superseded";
}

function VerificationOpsBody({ status }: { status: VerificationOpsViewDto }) {
  const s = status;
  const a = s.admission;
  const h = s.holdout;
  const k = s.killSwitch;
  const e = s.exportAudit;
  const o = s.openWeights;

  const headline: StatStripItem[] = [
    {
      title: "Holdout rows",
      value: h.readable && h.rows != null ? String(h.rows) : "N",
      intent: !h.readable ? "warning" : (h.rows ?? 0) >= 20 ? "success" : "warning",
      description: h.readable
        ? `${h.verified ?? 0} verified · ${h.waived ?? 0} waived · ${h.unjudged ?? 0} unjudged · ${h.tierCount ?? 0} tiers`
        : (h.blockedReason ?? "unreadable"),
    },
    {
      title: "Admission",
      value:
        a.readable && a.admittedProposals != null
          ? String(a.admittedProposals)
          : "N",
      intent: !a.readable ? "warning" : a.admittedProposals === 0 ? "neutral" : "success",
      description: a.readable
        ? `${a.admittedWithinQ ?? 0}/${a.admittedProposals ?? 0} within q≤0.05 · ${a.ledgerRows ?? 0} ledger rows · ${a.batches ?? 0} batches`
        : (a.blockedReason ?? "unreadable"),
    },
    {
      title: "Kill-switch",
      value: k.readable && k.phase ? k.phase : "N",
      intent: !k.readable
        ? "warning"
        : k.phase === "completed"
          ? k.reacted
            ? "success"
            : "neutral"
          : k.phase === "active"
            ? "info"
            : "neutral",
      description: k.readable
        ? [
            k.drillId ?? "no id",
            k.reacted == null ? null : k.reacted ? "reacted" : "no reaction",
            k.signalCount != null ? `${k.signalCount} signals` : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : (k.blockedReason ?? "unreadable"),
    },
    {
      title: "export.emitted",
      value: e.readable && e.count != null ? String(e.count) : "N",
      intent: !e.readable ? "warning" : e.count === 0 ? "neutral" : "info",
      description: e.readable
        ? "ops audit rows (INV-8 export door)"
        : (e.blockedReason ?? "unreadable"),
    },
    {
      title: "Open weights",
      value:
        o.readable && o.openInBoundary != null ? String(o.openInBoundary) : "N",
      intent: !o.readable ? "warning" : (o.openInBoundary ?? 0) > 0 ? "success" : "warning",
      description: o.readable
        ? `in-boundary · grandfather ${o.grandfathered ?? "—"}/${o.closedCensused ?? "—"} closed (${o.grandfatherSharePct != null ? `${o.grandfatherSharePct}%` : "N"} share)`
        : (o.blockedReason ?? "unreadable"),
    },
  ];

  // Prefer a compact 12-row grid: one row per M-id using the global cell when
  // present, otherwise the irreversible stratum, otherwise the first cell.
  const byId = new Map<string, ScorecardMetricCellDto[]>();
  for (const cell of s.metrics) {
    const list = byId.get(cell.id) ?? [];
    list.push(cell);
    byId.set(cell.id, list);
  }
  const gridRows: ScorecardMetricCellDto[] = [];
  for (let i = 1; i <= 12; i++) {
    const id = `M${i}`;
    const cells = byId.get(id) ?? [];
    if (cells.length === 0) continue;
    const preferred =
      cells.find((c) => c.stratum === "global") ??
      cells.find((c) => c.stratum === "irreversible") ??
      cells.find((c) => !c.blocked) ??
      cells[0]!;
    gridRows.push(preferred);
  }

  const metricCols: DataTableColumn<ScorecardMetricCellDto>[] = [
    {
      key: "id",
      header: "M",
      render: (r) => <span className={`${NUM} font-medium text-foreground`}>{r.id}</span>,
    },
    {
      key: "name",
      header: "Metric",
      render: (r) => (
        <div>
          <div className="text-foreground">{r.name}</div>
          <div className={MUTED}>
            {r.stratum}
            {r.n != null ? ` · n=${r.n}` : ""} · {r.provenance}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => metricStatusBadge(r),
    },
    {
      key: "display",
      header: "Value / blocked reason",
      render: (r) => (
        <span className={`${MUTED} whitespace-pre-wrap break-words`}>{r.display}</span>
      ),
    },
    {
      key: "target",
      header: "Target",
      render: (r) => <span className={MUTED}>{r.target}</span>,
    },
  ];

  const tierCols: DataTableColumn<HoldoutTierDto>[] = [
    {
      key: "tier",
      header: "Tier",
      render: (r) => <span className="text-foreground">{r.tier}</span>,
    },
    {
      key: "rows",
      header: "Rows",
      render: (r) => <span className={NUM}>{r.rows}</span>,
    },
    {
      key: "verified",
      header: "Verified",
      render: (r) => (
        <StatusBadge
          domain="verdict"
          status="accepted"
          label={String(r.verified)}
          withDot={r.verified > 0}
        />
      ),
    },
    {
      key: "waived",
      header: "Waived",
      render: (r) => (
        <StatusBadge
          domain="verdict"
          status="edited"
          label={String(r.waived)}
          withDot={r.waived > 0}
        />
      ),
    },
    {
      key: "unjudged",
      header: "Unjudged",
      render: (r) => (
        <StatusBadge
          domain="verdict"
          status="pending"
          label={String(r.unjudged)}
          withDot={r.unjudged > 0}
        />
      ),
    },
  ];

  // Full stratum matrix (all M × stratum cells) under the compact grid.
  const fullCols: DataTableColumn<ScorecardMetricCellDto>[] = [
    {
      key: "id",
      header: "M",
      render: (r) => <span className={NUM}>{r.id}</span>,
    },
    {
      key: "stratum",
      header: "Stratum",
      render: (r) => <span className="text-foreground">{r.stratum}</span>,
    },
    {
      key: "n",
      header: "n",
      render: (r) => (
        <span className={NUM}>{r.n == null ? "—" : r.n}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) =>
        r.blocked ? (
          <IntentBadge intent="warning" withDot>
            N
          </IntentBadge>
        ) : (
          <IntentBadge intent="success" withDot>
            Measured
          </IntentBadge>
        ),
    },
    {
      key: "display",
      header: "Display",
      render: (r) => (
        <span className={`${MUTED} whitespace-pre-wrap break-words`}>{r.display}</span>
      ),
    },
  ];

  return (
    <>
      <StatStrip items={headline} variant="board" />

      <DataTable
        title="§8 scorecard — M1–M12"
        subtitle={`window ${s.windowHours}h · generated ${formatRelativeTime(s.generatedAt)} · honest N when unmeasurable · preferred stratum shown (global → irreversible → first measured)`}
        rows={gridRows}
        columns={metricCols}
        rowKey={(r) => r.id}
        empty="No scorecard metrics returned."
      />

      <div className="grid grid-cols-1 gap-4 @4xl/page:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Holdout ledger</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            {!h.readable ? (
              <p className={MUTED}>{h.blockedReason}</p>
            ) : (
              <>
                <Row
                  label="Rows"
                  value={String(h.rows ?? "—")}
                />
                <Row
                  label="Judged"
                  value={`${(h.verified ?? 0) + (h.waived ?? 0)} (verified ${h.verified ?? 0} · waived ${h.waived ?? 0})`}
                />
                <Row label="Unjudged" value={String(h.unjudged ?? "—")} />
                <Row label="Tiers" value={String(h.tierCount ?? "—")} />
                <Row
                  label="Policy"
                  value={h.policyVersion != null ? `v${h.policyVersion}` : "—"}
                />
                <Row
                  label="Refreshed"
                  value={
                    h.refreshedAt ? formatRelativeTime(h.refreshedAt) : "—"
                  }
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle>Kill-switch last drill</CardTitle>
              {k.readable && k.phase ? (
                <StatusBadge
                  domain="verification_run"
                  status={killSwitchRunStatus(k.phase)}
                  label={k.phase}
                  withDot
                />
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            {!k.readable ? (
              <p className={MUTED}>{k.blockedReason}</p>
            ) : (
              <>
                <Row label="Drill id" value={k.drillId ?? "—"} />
                <Row
                  label="Workflow"
                  value={
                    k.taskId != null
                      ? `#${k.taskId}${k.workflowTitle ? ` · ${k.workflowTitle}` : ""}`
                      : (k.workflowTitle ?? "—")
                  }
                />
                <Row
                  label="Window"
                  value={
                    k.start
                      ? `${formatRelativeTime(k.start)}${k.end ? ` → ${formatRelativeTime(k.end)}` : ""}`
                      : "—"
                  }
                />
                <Row
                  label="Reaction"
                  value={
                    k.reacted == null
                      ? "—"
                      : k.reacted
                        ? `reacted (${k.signalCount ?? 0} signals · ${k.samples ?? 0} samples)`
                        : `no reaction (${k.samples ?? 0} samples)`
                  }
                  intent={
                    k.reacted == null ? "neutral" : k.reacted ? "success" : "warning"
                  }
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Admission (M1 ledger)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            {!a.readable ? (
              <p className={MUTED}>{a.blockedReason}</p>
            ) : (
              <>
                <Row label="Ledger rows" value={String(a.ledgerRows ?? "—")} />
                <Row
                  label="Admitted"
                  value={String(a.admittedProposals ?? "—")}
                />
                <Row
                  label="Within q≤0.05"
                  value={String(a.admittedWithinQ ?? "—")}
                />
                <Row label="Batches" value={String(a.batches ?? "—")} />
                <Row
                  label="Unparsable"
                  value={String(a.unparsable ?? "—")}
                  intent={(a.unparsable ?? 0) > 0 ? "warning" : "neutral"}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Export audit · open weights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 type-body">
            {!e.readable ? (
              <p className={MUTED}>{e.blockedReason}</p>
            ) : (
              <Row
                label="export.emitted"
                value={String(e.count ?? "—")}
              />
            )}
            {!o.readable ? (
              <p className={MUTED}>{o.blockedReason}</p>
            ) : (
              <>
                <Row
                  label="Open in-boundary"
                  value={String(o.openInBoundary ?? "—")}
                />
                <Row
                  label="Closed censused"
                  value={String(o.closedCensused ?? "—")}
                />
                <Row
                  label="Grandfathered"
                  value={String(o.grandfathered ?? "—")}
                />
                <Row
                  label="Grandfather share"
                  value={
                    o.grandfatherSharePct != null
                      ? `${o.grandfatherSharePct}%`
                      : "N"
                  }
                />
                {o.openIds.length > 0 ? (
                  <p className={MUTED}>open: {o.openIds.join(", ")}</p>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {h.readable && h.tiers.length > 0 ? (
        <DataTable
          title="Holdout tiers"
          subtitle="family strata from the holdout ledger machine block — not §9.4 capability tiers"
          rows={h.tiers}
          columns={tierCols}
          rowKey={(r) => r.tier}
          empty="No tiers in the ledger."
        />
      ) : null}

      <DataTable
        title="Full M1–M12 × stratum matrix"
        subtitle="every cell from the shared scorecard-metrics registry · blocked rows keep their reason"
        rows={s.metrics}
        columns={fullCols}
        rowKey={(r) => `${r.id}:${r.stratum}`}
        empty="No metric cells."
      />

      {s.notes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className={`${MUTED} list-disc space-y-1 pl-4`}>
              {s.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}

function Row({
  label,
  value,
  intent,
}: {
  label: string;
  value: string;
  intent?: "neutral" | "danger" | "warning" | "success";
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="type-subtext text-muted-foreground">{label}</span>
      {intent && intent !== "neutral" ? (
        <IntentBadge intent={intent}>{value}</IntentBadge>
      ) : (
        <span className={`${NUM} text-foreground text-right break-words`}>{value}</span>
      )}
    </div>
  );
}

export const agentsVerificationOpsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/verification-ops",
  component: AgentsVerificationOpsPage,
});
